import {
  Color,
  CanvasTexture,
  FramebufferTexture,
  LinearFilter,
  Mesh,
  Vector2,
  Texture,
  type WebGLRenderTarget,
  LinearToneMapping,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  PMREMGenerator,
  Quaternion,
  Scene,
  SRGBColorSpace,
  Vector3,
  VSMShadowMap,
  WebGLRenderer,
} from 'three';
import type { FrameSpec } from '../config/frames';
import { verticalFovDeg, type CameraIntrinsics } from '../pose/CameraIntrinsics';
import { acetateTransparentSort } from './AcetateOptics';
import { createStudioEnvironment } from './environment';
import { disposeWarmup } from './ShaderWarmup';
import { ScreenLensMaterial } from './LensOptics';
import type { FrameObject } from './FrameObject';
import { HeadOccluder } from './HeadOccluder';
import { TempleClip } from './TempleClip';

/** Shadow map side (the shadow camera spans 260 mm: 0.5 mm per texel). */
const SHADOW_MAP_SIZE = 512;
/** Gaussian blur of the variance shadow map, in texels (≈ 5 mm penumbra at 512). */
const SHADOW_BLUR_TEXELS = 10;
/**
 * A110Y: cap on the drawing buffer, in pixels. The buffer is the video resolution × pixel ratio
 * (cap 2 at the high level): 2560×1440 for a 720p camera — unchanged, the cap is exactly that —
 * but 3840×2160 (up to 350 MB of GPU memory for the canvas alone) if a phone hands out a 1080p
 * camera: that case is brought back to 3.7 MP. Nothing is rendered smaller than before.
 */
const MAX_DRAWING_PIXELS = 3.7e6;

/** Resource counters of the WebGL renderer (debug panel, `tryon_close`). */
export interface RendererStats {
  textures: number;
  geometries: number;
  programs: number;
  calls: number;
  triangles: number;
  /** Drawing buffer, in pixels. */
  bufferPixels: number;
}

/**
 * Three.js overlay renderer.
 *
 * The transparent WebGL canvas sits exactly over the <video> (same aspect,
 * same `object-fit: cover`), and the virtual camera reproduces the assumed
 * intrinsics of the real one, so a point unprojected by the pose solver lands
 * on the same pixel as the landmark it came from.
 *
 * Units are millimetres throughout (near 10 mm, far 3 m).
 */
/**
 * A110Y: a video texture without three's perpetual requestVideoFrameCallback chain (never cancelled
 * by dispose(); one more callback per camera frame at every lock → live switch). `isVideoTexture`
 * keeps three's video upload path and the sRGB « video » mode of AcetateOptics; the app sets
 * `needsUpdate` from its own video-frame callback.
 */
class LiveVideoTexture extends Texture {
  readonly isVideoTexture = true;
  constructor(video: HTMLVideoElement) {
    super(video);
  }
  /** three calls this before each use of a video texture: nothing to do, the app drives the uploads. */
  update(): void {}
}

function prepareCameraTexture<T extends Texture>(t: T): T {
  t.colorSpace = SRGBColorSpace;
  t.generateMipmaps = false;
  t.minFilter = t.magFilter = LinearFilter;
  return t;
}

export class Renderer {
  readonly gl: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(50, 1, 10, 3000);
  /** Depth-only head geometry that hides the frame where the head is in front of it. */
  readonly occluder = new HeadOccluder();
  /** Temples end at the occluder's ear: nothing of them is drawn behind it (see TempleClip). */
  readonly templeClip = new TempleClip();
  private frame: FrameObject | null = null;
  /**
   * A110Y: one texture per camera source for the whole session. Before, a new VideoTexture was
   * created at every frame-lock → live switch; three uploads a video only on its next *presented*
   * frame, so for 1–4 screen frames (or hundreds of ms when the camera was paused by the OS) the
   * background was an empty texture: the camera copy behind the lenses was black and the lenses
   * were drawn black and opaque. Now the live texture is driven by the app's own video-frame
   * callback and the background only switches to it once a new frame has been uploaded; until
   * then the last locked frame stays on screen.
   */
  private videoTexture: LiveVideoTexture | null = null;
  private canvasTexture: CanvasTexture | null = null;
  private canvasTextureSize = { w: 0, h: 0 };
  private sceneCopy: FramebufferTexture | null = null;
  private readonly bufferSize = new Vector2();
  private readonly lenses: Mesh[] = [];
  private environmentTarget: WebGLRenderTarget;
  private focalPx = 1000;
  /** Set false for an A/B against the legacy alpha overlay. */
  physicalLenses = true;
  private readonly key: DirectionalLight;
  private readonly fill: DirectionalLight;
  private readonly hemi: HemisphereLight;
  private readonly keyDir = new Vector3();
  private readonly tmpDir = new Vector3();
  private exposure = 1;
  private readonly tint = new Color(1, 1, 1);
  private readonly keyBase = new Color(1, 1, 1);
  private readonly fillBase = new Color(1, 1, 1);
  private readonly tmpColor = new Color();
  private pixelRatio = 1;
  /** A110Y: pixel ratio actually applied once the drawing-buffer budget is taken into account. */
  private effectivePixelRatio = 1;
  private videoWidth = 0;
  private videoHeight = 0;
  private readonly tmpQ = new Quaternion();
  /** A110W: shadow pass on/off (the light keeps casting: see setShadowMapSize). */
  private shadowPass = true;
  private disposed = false;

  constructor(readonly canvas: HTMLCanvasElement) {
    // Stencil: library acetates multiply/add each pixel once (see AcetateOptics).
    // A110Y: rendering options unchanged on purpose (anti-aliasing, high-performance GPU): the
    // stability work must not cost a pixel of the image David validated.
    this.gl = new WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance', premultipliedAlpha: true, stencil: true });
    this.gl.setClearColor(0x000000, 0);
    this.gl.outputColorSpace = SRGBColorSpace;
    this.gl.toneMapping = LinearToneMapping; // neutral for the photo textures: Neutral/ACES crush and over-saturate a dark olive (black offset), the highlights may clip
    this.gl.toneMappingExposure = 1.0;
    // Library acetates: each transmission (multiply) draw right before its lit draw.
    this.gl.setTransparentSort(acetateTransparentSort);
    // Material clipping planes: the temple cut at the ear (TempleClip).
    this.gl.localClippingEnabled = true;

    // Image-based lighting without any external asset: a soft studio with a
    // large key softbox, a rim light and a neutral floor — the highlights a
    // glossy acetate needs to read as glossy.
    this.environmentTarget = this.buildEnvironment();
    // A110X: phones drop the WebGL context of a page left in the background.
    // three.js re-creates its GPU state on restore; what was *drawn* on the GPU
    // (the studio environment) must be drawn again. The app is told both ways.
    canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);

    // Ambient: sky above, and below it the wearer's own face and clothes — a
    // frame sits a centimetre above skin, so its downward edges are lit by a
    // warm bounce, not by a dim floor.
    this.hemi = new HemisphereLight(0xffffff, 0xa89890, 0.3);
    this.scene.add(this.hemi);
    this.key = new DirectionalLight(0xfff8f2, 0.8);
    this.keyBase.copy(this.key.color);
    this.key.position.set(-60, 160, 420); // front, a little above: the shadow hugs the rims instead of falling on the cheeks
    this.scene.add(this.key);
    this.scene.add(this.key.target);
    this.fill = new DirectionalLight(0xe6eeff, 0.4);
    this.fillBase.copy(this.fill.color);
    this.fill.position.set(220, 60, 380);
    this.scene.add(this.fill);

    // The key light casts the frame's shadow onto the face (shadow catcher in
    // the occluder). Variance shadow map: a real Gaussian penumbra of a few
    // millimetres — the coarse landmark mesh that receives the shadow would
    // print every one of its facets into a hard-edged shadow — and no acne on
    // the temples, which lie almost parallel to the light. The orthographic
    // shadow camera follows the frame each render.
    this.gl.shadowMap.enabled = true;
    this.gl.shadowMap.type = VSMShadowMap;
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    this.key.shadow.radius = SHADOW_BLUR_TEXELS;
    this.key.shadow.blurSamples = 12;
    this.key.shadow.bias = -0.0002;
    const cam = this.key.shadow.camera;
    cam.left = -130;
    cam.right = 130;
    cam.top = 130;
    cam.bottom = -130;
    cam.near = 50;
    cam.far = 900;
    cam.updateProjectionMatrix();
    this.keyDir.copy(this.key.position).normalize();

    this.scene.add(this.occluder.root);
    this.key.layers.enable(1);
    this.fill.layers.enable(1);
    this.hemi.layers.enable(1);
  }

  /** Match the canvas + virtual camera to the video stream. */
  setVideo(width: number, height: number, intrinsics: CameraIntrinsics): void {
    this.focalPx = intrinsics.f;
    this.videoWidth = width;
    this.videoHeight = height;
    this.camera.aspect = width / height;
    this.camera.fov = verticalFovDeg(intrinsics);
    this.camera.updateProjectionMatrix();
    this.applySize();
  }

  setPixelRatio(ratio: number): void {
    this.pixelRatio = ratio;
    this.applySize();
  }

  /**
   * Adapts the rendering to the real scene, measured on the video: exposure
   * from the face luminance (a frame lit like a studio product shot in a dim
   * room reads as pasted on), and a colour cast from the frame's mean colour
   * (warm room, cool daylight). Smoothed: the user never sees it adapt.
   */
  setSceneLight(faceLuma: number, tint: { r: number; g: number; b: number }, dirX = 0, dirY = 0.3): void {
    const targetExposure = Math.min(1.35, Math.max(0.45, faceLuma / 125));
    this.exposure += (targetExposure - this.exposure) * 0.25;
    this.gl.toneMappingExposure = this.exposure;
    // Key light from the side the face is lit from (image x = camera-space x),
    // in front and a little above (17°–32°): steeper, and the frame's shadow
    // lands centimetres down the cheeks, cut off from the rims that cast it,
    // while a front tilted down by the pantoscopic angle falls into the dark.
    // A small downward offset makes the soft rim shadow readable on the upper cheek.
    this.tmpDir.set(dirX * 0.7, 0.42 + Math.max(0, dirY) * 0.3, 0.95).normalize();
    this.keyDir.lerp(this.tmpDir, 0.15).normalize();
    // A third of the cast: the camera has already white-balanced most of the
    // room's colour, and full strength turns a blue-green olive yellow.
    this.tmpColor.setRGB(1 + (tint.r - 1) * 0.35, 1 + (tint.g - 1) * 0.35, 1 + (tint.b - 1) * 0.35);
    this.tint.lerp(this.tmpColor, 0.25);
    this.key.color.copy(this.tint).multiply(this.keyBase);
    this.fill.color.copy(this.tint).multiply(this.fillBase);
    this.hemi.color.copy(this.tint);
  }

  /**
   * Shadow map resolution (0 disables the frame's shadow on the face); capped at the VSM size.
   * A110W: 0 skips the shadow pass and hides the shadow catcher instead of turning the light's
   * castShadow off — that would change every lit program and three would recompile them all,
   * synchronously, in the middle of the session (a freeze of several hundred ms on phones).
   */
  setShadowMapSize(size: number): void {
    const enable = size > 0;
    const mapSize = Math.min(SHADOW_MAP_SIZE, size);
    this.shadowPass = enable;
    if (enable && this.key.shadow.mapSize.x !== mapSize) {
      this.key.shadow.mapSize.set(mapSize, mapSize);
      this.key.shadow.radius = Math.max(4, Math.round((SHADOW_BLUR_TEXELS * mapSize) / SHADOW_MAP_SIZE));
      this.key.shadow.map?.dispose();
      this.key.shadow.map = null;
    }
    this.occluder.setShadowEnabled(enable);
  }

  private applySize(): void {
    if (this.videoWidth === 0 || this.disposed) return;
    // A110Y: the requested ratio, capped so the drawing buffer stays under the pixel budget.
    const budget = Math.sqrt(MAX_DRAWING_PIXELS / (this.videoWidth * this.videoHeight));
    this.effectivePixelRatio = Math.min(this.pixelRatio, Math.max(0.5, budget));
    this.gl.setPixelRatio(this.effectivePixelRatio);
    // Drawing buffer = video resolution × pixel ratio; CSS size is handled by the stylesheet.
    this.gl.setSize(this.videoWidth, this.videoHeight, false);
    this.gl.getDrawingBufferSize(this.bufferSize);
    if (!this.sceneCopy || this.sceneCopy.image.width !== this.bufferSize.x || this.sceneCopy.image.height !== this.bufferSize.y) {
      this.sceneCopy?.dispose();
      this.sceneCopy = new FramebufferTexture(this.bufferSize.x, this.bufferSize.y);
      // Canvas framebuffer is RGBA8. Copy to matching storage and decode sRGB in the lens shader.
      this.sceneCopy.minFilter = this.sceneCopy.magFilter = LinearFilter;
    }
  }

  setFrame(frame: FrameObject | null): void {
    if (this.frame) this.scene.remove(this.frame.root);
    this.frame = frame;
    this.lenses.length = 0;
    if (frame) {
      this.scene.add(frame.root);
      this.templeClip.attach(frame); // compiled with the cut from its first draw
      frame.root.traverse(o => { if (o instanceof Mesh && o.material instanceof ScreenLensMaterial) this.lenses.push(o); });
    }
  }

  getFrame(): FrameObject | null {
    return this.frame;
  }

  setOccluderEnabled(enabled: boolean): void {
    this.occluder.setParams({ enabled });
  }

  /**
   * Renders one frame.
   * @param position frame origin in camera space (mm) or null to hide
   * @param quaternion frame orientation (smoothed, includes the pantoscopic tilt)
   * @param spec frame spec (hinge geometry for the occluder)
   * @param opacity fade 0..1
   * @param templeSpreadDeg temple opening
   * @param headWidthMm measured head width for the occluder (null → default)
   */
  render(position: Vector3 | null, quaternion: Quaternion | null, spec: FrameSpec, opacity: number, templeSpreadDeg: number, headWidthMm: number | null): void {
    const frame = this.frame;
    const visible = position !== null && quaternion !== null && opacity > 0;
    if (frame) {
      if (visible) {
        frame.root.position.copy(position);
        frame.root.quaternion.copy(quaternion);
        frame.setTempleSpread(templeSpreadDeg);
        frame.setTempleTilt(spec.fit.pantoscopicTiltDeg);
        frame.setOpacity(opacity);
        // Same pose and tilt as the occluder below: the cut sits in its ear.
        this.templeClip.update(frame, position, quaternion, spec.fit.pantoscopicTiltDeg);
        // Keep the shadow camera centred on the frame (directional light: only the direction matters).
        this.key.target.position.copy(position);
        this.key.position.copy(this.keyDir).multiplyScalar(450).add(position);
      } else {
        frame.setOpacity(0);
      }
    }
    // Resizing a handmade frame must never resize the wearer or relocate ears.
    const headSpec = frame ? { ...frame.spec, fit: spec.fit } : spec;
    this.occluder.pose(visible ? position : null, visible ? quaternion : null, headSpec, headWidthMm, opacity);
    const composite = this.physicalLenses && this.sceneCopy !== null && this.scene.background !== null;
    // A110Y: never compose the lenses on a background that has no image on the GPU (the copy
    // would be black): that frame, the lenses are simply not drawn.
    const bgReady = this.backgroundReady;
    for (const lens of this.lenses) {
      const mat = lens.material as ScreenLensMaterial;
      lens.layers.set(composite ? 1 : 0);
      lens.visible = !composite || bgReady;
      mat.optics.mataComposite.value = composite ? 1 : 0;
      // Overlay-only/lab fallback preserves the original transparency contract.
      const legacyOpacity = Math.min(0.95, Math.max(0.05, spec.look.lensOpacity));
      mat.opacity = composite ? opacity : opacity * legacyOpacity;
      mat.envMapIntensity = composite ? 0.65 : (0.55 + 0.6 * spec.look.lensMirror) / legacyOpacity;
      mat.optics.mataScene.value = this.sceneCopy;
      mat.optics.mataViewport.value.copy(this.bufferSize);
      mat.optics.mataExposure.value = this.exposure;
      mat.optics.mataFocalPx.value = this.focalPx * this.effectivePixelRatio;
      mat.optics.mataLensHeight.value = spec.dims.lensHeightMm;
      mat.optics.mataLensCentreY.value = 0;
      if (lens.userData.templeLens === true) {
        if (!lens.geometry.boundingBox) lens.geometry.computeBoundingBox();
        const bounds = lens.geometry.boundingBox!;
        mat.optics.mataLensCentreY.value = (bounds.min.y + bounds.max.y) / 2;
        mat.optics.mataLensHeight.value = bounds.max.y - bounds.min.y;
      }
    }
    this.camera.layers.set(0);
    this.gl.shadowMap.autoUpdate = this.shadowPass;
    this.gl.render(this.scene, this.camera);
    if (composite && visible && bgReady && this.lenses.length > 0) {
      // A110Y2 (Astra A146): with side lenses (Velvet Noir) each lens is composed in its own pass below.
      if (!this.lenses.some(lens => lens.userData.templeLens === true)) this.gl.copyFramebufferToTexture(this.sceneCopy!);
      const background = this.scene.background;
      this.scene.background = null;
      this.gl.autoClear = false;
      this.camera.layers.set(1);
      // Reuse the first pass shadow map; only the lenses are drawn again.
      this.gl.shadowMap.autoUpdate = false;
      try {
        if (this.lenses.some(lens => lens.userData.templeLens === true)) {
          // Four lenses (two in the temples): drawn from the farthest to the nearest, each on a fresh
          // copy of what is already drawn, so overlapping lenses keep their combined tint.
          const states = this.lenses.map(lens => ({ lens, visible: lens.visible }));
          const ordered = states.filter(item => item.visible).map(item => {
            if (!item.lens.geometry.boundingBox) item.lens.geometry.computeBoundingBox();
            const centre = item.lens.geometry.boundingBox!.getCenter(new Vector3());
            item.lens.localToWorld(centre).applyMatrix4(this.camera.matrixWorldInverse);
            return { ...item, z: centre.z };
          }).sort((a, b) => a.z - b.z);
          for (const item of states) item.lens.visible = false;
          try {
            for (const item of ordered) {
              this.gl.copyFramebufferToTexture(this.sceneCopy!);
              item.lens.visible = true;
              this.gl.render(this.scene, this.camera);
              item.lens.visible = false;
            }
          } finally { for (const item of states) item.lens.visible = item.visible; }
        } else this.gl.render(this.scene, this.camera);
      } finally {
        this.gl.shadowMap.autoUpdate = this.shadowPass;
        this.gl.autoClear = true;
        this.camera.layers.set(0);
        this.scene.background = background;
      }
    }
  }

  /**
   * Shows the camera behind the frame: the live <video>, or the frame-locked canvas (the exact
   * image the displayed landmarks came from). `changed`: a new image is available on that source
   * since the last call (a camera frame arrived, or the locked canvas was repainted).
   */
  setCameraFrame(source: HTMLVideoElement | HTMLCanvasElement, changed = true): void {
    if (source instanceof HTMLCanvasElement) {
      // three allocates immutable storage (texStorage2D): a canvas of another size needs a new texture.
      if (this.canvasTexture && (this.canvasTextureSize.w !== source.width || this.canvasTextureSize.h !== source.height)) {
        this.canvasTexture.dispose();
        this.canvasTexture = null;
      }
      if (!this.canvasTexture) {
        this.canvasTexture = prepareCameraTexture(new CanvasTexture(source));
        this.canvasTextureSize = { w: source.width, h: source.height };
        changed = true;
      }
      if (changed) this.canvasTexture.needsUpdate = true;
      this.scene.background = this.canvasTexture;
    } else {
      if (!this.videoTexture || this.videoTexture.image !== source) {
        this.videoTexture?.dispose();
        this.videoTexture = prepareCameraTexture(new LiveVideoTexture(source));
      }
      const tex = this.videoTexture;
      if (changed) tex.needsUpdate = true;
      // Switch to the live video only once it carries a fresh image (uploaded this frame or
      // earlier); otherwise the previous background (last locked frame) stays until it does.
      if (changed || tex.version > 0 || this.scene.background === null) this.scene.background = tex;
    }
    this.canvas.classList.add('mt-canvas--composite');
  }

  /** The background texture has at least one image on the GPU (otherwise the lens copy would be black). */
  get backgroundReady(): boolean {
    const bg = this.scene.background;
    return bg instanceof Texture && bg.version > 0;
  }

  /** Captures the current composite (video or frame-locked canvas + overlay) into a canvas. Used by Phase 3 photo mode. */
  snapshot(video: HTMLVideoElement | HTMLCanvasElement, mirrored: boolean, target?: HTMLCanvasElement | null): HTMLCanvasElement {
    // A110Y: the caller may hand back the same canvas for every shot (one 1280×720 canvas per
    // photo before, released only at the garbage collection).
    const out = target ?? document.createElement('canvas');
    if (out.width !== this.videoWidth || out.height !== this.videoHeight) {
      out.width = this.videoWidth;
      out.height = this.videoHeight;
    }
    const ctx = out.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, out.width, out.height);
    if (mirrored) {
      ctx.translate(out.width, 0);
      ctx.scale(-1, 1);
    }
    // A110Y: always the camera first (the composite canvas covers it when it carries the camera;
    // a photo taken on an empty background keeps the camera instead of black lenses).
    ctx.drawImage(video, 0, 0, out.width, out.height);
    ctx.filter = this.backgroundReady ? 'none' : 'blur(0.85px) saturate(0.93)'; // same camera-matching filter as the CSS overlay
    ctx.drawImage(this.canvas, 0, 0, out.width, out.height);
    ctx.filter = 'none';
    return out;
  }

  /** Studio lighting for the glossy acetates: rendered on the GPU (PMREM), so rebuilt after a context loss. */
  private buildEnvironment(): WebGLRenderTarget {
    const pmrem = new PMREMGenerator(this.gl);
    const studio = createStudioEnvironment();
    const target = pmrem.fromScene(studio, 0.04);
    this.scene.environment = target.texture;
    studio.traverse(o => { if (o instanceof Mesh) { o.geometry.dispose(); const ms = Array.isArray(o.material) ? o.material : [o.material]; ms.forEach(m => m.dispose()); } });
    pmrem.dispose();
    return target;
  }

  private lostContext = false;
  private readonly contextListeners: Array<(lost: boolean) => void> = [];

  private readonly onContextLost = (): void => {
    this.lostContext = true;
    for (const l of this.contextListeners) l(true);
  };

  private readonly onContextRestored = (): void => {
    // three.js has re-initialised its state (its own listener runs first).
    this.lostContext = false;
    const old = this.environmentTarget;
    this.environmentTarget = this.buildEnvironment();
    old.dispose();
    this.resetSceneCopy();
    for (const l of this.contextListeners) l(false);
  };

  /** Drops the copy of the drawn scene: it is re-created on the next draw. */
  private resetSceneCopy(): void {
    this.sceneCopy?.dispose();
    this.sceneCopy = null;
    this.applySize();
  }

  /** A110X: the WebGL context is lost (nothing can be drawn until the browser restores it). */
  get contextLost(): boolean {
    return this.lostContext || this.gl.getContext().isContextLost();
  }

  /** A110X: `listener(true)` when the context is lost, `listener(false)` once it is restored. */
  onContextChange(listener: (lost: boolean) => void): () => void {
    this.contextListeners.push(listener);
    return () => {
      const i = this.contextListeners.indexOf(listener);
      if (i >= 0) this.contextListeners.splice(i, 1);
    };
  }

  /** A110Y: resource counters (textures and programs must stay flat across frames and acetates). */
  stats(): RendererStats {
    const info = this.gl.info;
    return {
      textures: info.memory.textures,
      geometries: info.memory.geometries,
      programs: info.programs?.length ?? 0,
      calls: info.render.calls,
      triangles: info.render.triangles,
      bufferPixels: this.bufferSize.x * this.bufferSize.y,
    };
  }

  /** Pixel ratio really applied to the drawing buffer (A110Y: after the pixel budget). */
  get appliedPixelRatio(): number {
    return this.effectivePixelRatio;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored, false);
    this.contextListeners.length = 0;
    this.scene.background = null;
    this.scene.environment = null;
    this.frame?.dispose();
    this.frame = null;
    this.lenses.length = 0;
    this.occluder.dispose();
    this.videoTexture?.dispose();
    this.videoTexture = null;
    this.canvasTexture?.dispose();
    this.canvasTexture = null;
    this.sceneCopy?.dispose();
    this.sceneCopy = null;
    this.environmentTarget.dispose();
    this.key.shadow.map?.dispose();
    this.key.shadow.mapPass?.dispose();
    disposeWarmup(this.gl);
    this.gl.dispose();
    // A110Y: three's dispose() keeps the WebGL context alive until the canvas is garbage-collected
    // — late on Safari, and Chrome drops the oldest context of a page past 16 live ones. Release
    // it now, and shrink the canvas so its drawing buffer (up to 150 MB) is returned at once.
    try {
      this.gl.forceContextLoss();
    } catch {
      /* context already gone */
    }
    this.canvas.width = 1;
    this.canvas.height = 1;
  }

  /** Exposed for tests / debug. */
  get tmpQuaternion(): Quaternion {
    return this.tmpQ;
  }
}
